// TemplateEditor — surface shown when a template is selected in the
// Templates explorer. Mirrors `DashboardEditor` (render / configure
// toggle, server-is-truth code seeding) and adds a `default_values`
// JSON editor next to the TSX editor in configure mode.
//
// The render preview spreads the saved `default_values` into the
// template's component as initial props — so the editor shows the
// same first-paint a caller using `<Template path="..." />` would
// see when no override props are supplied. Templates can also
// instantiate other templates, so the registry is forwarded into
// the sandbox just like on the dashboards side.
//
// "Save" persists `code` and `default_values` together via the
// proxy's atomic `PUT /templates/{id}/content` endpoint. The
// defaults editor validates JSON locally before save so a malformed
// payload can't reach the server.

import { useEffect, useMemo, useState } from 'react'
import {
  Check,
  Eye,
  FolderInput,
  Loader2,
  MoreHorizontal,
  Pencil,
  Save,
  Settings2,
  Trash2
} from 'lucide-react'
import {
  useListTemplatesQuery,
  useUpdateTemplateContentMutation,
  type Template
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'
import { Label } from '../../../components/ui/label'
import CodeEditorPane from './CodeEditorPane'
import type { EditorMode } from './editor-mode'
import SandboxFrame from '../../../components/DashboardSandbox/SandboxFrame'
import { TEMPLATE_STARTER_CODE } from './template'

/** Seed value for the editor: saved code, or the starter when empty. */
function initialCode(template: Template): string {
  return template.code !== '' ? template.code : TEMPLATE_STARTER_CODE
}

/**
 * Pretty-print a default-values object for the JSON pane. We always
 * round-trip through JSON.stringify so freshly-saved defaults render
 * with stable indentation (the proxy normalizes on its side too,
 * but we don't depend on that here).
 */
function formatDefaults(values: Record<string, unknown>): string {
  return JSON.stringify(values, null, 2)
}

export interface TemplateEditorProps {
  template: Template
  /** Owned by the page so it can offer the mode as chat context. */
  mode: EditorMode
  onModeChange: (mode: EditorMode) => void
  onRename?: () => void
  onMove?: () => void
  onDelete?: () => void
}

const TemplateEditor = ({
  template,
  mode,
  onModeChange,
  onRename,
  onMove,
  onDelete
}: TemplateEditorProps) => {
  const [code, setCode] = useState(() => initialCode(template))
  const [defaultsText, setDefaultsText] = useState(() => formatDefaults(template.default_values))
  // Last persisted server values — used to detect dirtiness.
  const [savedCode, setSavedCode] = useState(code)
  const [savedDefaultsText, setSavedDefaultsText] = useState(defaultsText)
  const [defaultsError, setDefaultsError] = useState<string | null>(null)
  const [renderEpoch, setRenderEpoch] = useState(0)

  const [updateContent, { isLoading: isSaving, isError: saveFailed }] =
    useUpdateTemplateContentMutation()

  // Forward saved templates into the sandbox so a template can also
  // compose other templates via `<Template path="..." />`. The
  // currently-edited template is included too — the live `code` in
  // the editor takes precedence over the persisted entry, which we
  // achieve by overlaying it last.
  const templatesQuery = useListTemplatesQuery()
  const templateRegistry = useMemo(() => {
    const base = (templatesQuery.data ?? []).map((t) => ({
      path: t.path,
      code: t.code,
      defaultValues: t.default_values ?? {}
    }))
    // Drop any persisted entry for THIS template's path so the
    // unsaved buffer wins for self-references during preview.
    const withoutSelf = base.filter((entry) => entry.path !== template.path)
    return [
      ...withoutSelf,
      { path: template.path, code, defaultValues: template.default_values ?? {} }
    ]
  }, [templatesQuery.data, template.path, template.default_values, code])

  // Parse the defaults editor's text into a real object, but only when
  // it changes — used both for the live preview's props and for the
  // dirty-state diff.
  const parsedDefaults = useMemo<Record<string, unknown>>(() => {
    try {
      const value = JSON.parse(defaultsText)
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return template.default_values
      }
      return value as Record<string, unknown>
    } catch {
      // Fall back to the last-known-good defaults so the preview
      // doesn't blank out while the user is mid-edit.
      return template.default_values
    }
  }, [defaultsText, template.default_values])

  // Re-hydrate (and drop back to render mode) when the user switches
  // to a different template. Mirrors DashboardEditor's policy of
  // pinning re-seeds to the row id so a list refetch doesn't clobber
  // unsaved edits.
  useEffect(() => {
    const nextCode = initialCode(template)
    const nextDefaults = formatDefaults(template.default_values)
    setCode(nextCode)
    setDefaultsText(nextDefaults)
    setSavedCode(nextCode)
    setSavedDefaultsText(nextDefaults)
    setRenderEpoch((epoch) => epoch + 1)
    onModeChange('render')
    setDefaultsError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id])

  // Adopt server-side content when clean so chat edits and refetches
  // refresh the live preview without clobbering unsaved local edits.
  useEffect(() => {
    if (code !== savedCode || defaultsText !== savedDefaultsText) return
    const nextCode = initialCode(template)
    const nextDefaults = formatDefaults(template.default_values)
    if (nextCode === code && nextDefaults === defaultsText) return
    setCode(nextCode)
    setDefaultsText(nextDefaults)
    setSavedCode(nextCode)
    setSavedDefaultsText(nextDefaults)
    setRenderEpoch((epoch) => epoch + 1)
  }, [
    template.code,
    template.default_values,
    template.id,
    code,
    savedCode,
    defaultsText,
    savedDefaultsText
  ])

  const isDirty = code !== savedCode || defaultsText !== savedDefaultsText

  const handleSave = async (): Promise<void> => {
    let parsed: Record<string, unknown>
    try {
      const value = JSON.parse(defaultsText)
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        setDefaultsError('Default values must be a JSON object (not an array, scalar, or null).')
        return
      }
      parsed = value as Record<string, unknown>
    } catch (err) {
      setDefaultsError(`Default values must be valid JSON: ${(err as Error).message}`)
      return
    }
    setDefaultsError(null)

    try {
      await updateContent({
        id: template.id,
        input: { code, default_values: parsed }
      }).unwrap()
      setSavedCode(code)
      setSavedDefaultsText(defaultsText)
      setRenderEpoch((epoch) => epoch + 1)
    } catch {
      // Surfaced via the saveFailed flag in the header.
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-line px-6 py-3">
        <h1 className="m-0 text-lg font-semibold text-ink">{template.name}</h1>
        <code className="rounded bg-bg-sunk px-2 py-0.5 font-mono text-11 text-ink-3">
          /{template.path}
        </code>
        <div className="flex-1" />

        {saveFailed ? (
          <span className="text-11 text-st-down">Couldn’t save</span>
        ) : isDirty ? (
          <span className="text-11 text-ink-3">Unsaved changes</span>
        ) : (
          <span className="flex items-center gap-1 text-11 text-ink-3">
            <Check size={12} aria-hidden />
            Saved
          </span>
        )}

        <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </Button>

        {onRename || onMove || onDelete ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Template actions"
                data-testid="template-editor-overflow"
              >
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onRename ? (
                <DropdownMenuItem onSelect={onRename}>
                  <Pencil />
                  Rename
                </DropdownMenuItem>
              ) : null}
              {onMove ? (
                <DropdownMenuItem onSelect={onMove}>
                  <FolderInput />
                  Move to…
                </DropdownMenuItem>
              ) : null}
              {onDelete ? (
                <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {mode === 'render' ? (
          <Button variant="outline" size="sm" onClick={() => onModeChange('configure')}>
            <Settings2 size={14} />
            Configure
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => onModeChange('render')}>
            <Eye size={14} />
            View Render
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {mode === 'render' ? (
          <SandboxFrame
            key={renderEpoch}
            code={code}
            props={parsedDefaults}
            templates={templateRegistry}
          />
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-hidden border-b border-line">
              <CodeEditorPane code={code} onChange={setCode} />
            </div>
            <div className="flex max-h-[40%] min-h-35 flex-col gap-1.5 px-6 py-3">
              <div className="flex items-center gap-2">
                <Label className="text-11 uppercase tracking-wider text-ink-3">
                  Default values (JSON object)
                </Label>
                <span className="font-mono text-11 text-ink-3">
                  spread as initial props at render time
                </span>
              </div>
              <textarea
                aria-label="Default values JSON"
                value={defaultsText}
                onChange={(e) => {
                  setDefaultsText(e.target.value)
                  if (defaultsError !== null) setDefaultsError(null)
                }}
                spellCheck={false}
                className="min-h-20 flex-1 resize-none rounded-md border border-line bg-bg-elev px-3 py-2 font-mono text-13 leading-snug text-ink outline-none focus:border-brand"
              />
              {defaultsError ? (
                <div className="rounded-md bg-st-down-bg p-2 text-12 text-st-down" role="alert">
                  {defaultsError}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplateEditor
